import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'react-hot-toast';
import Pagination from '../components/Pagination';
import CategoryDragDrop from '../components/CategoryDragDrop';
import { useNavigate } from 'react-router-dom';

const LEVELS = [
  { value: 1, label: 'Level 1 — Main Section', example: 'e.g. EXPENDITURE' },
  { value: 2, label: 'Level 2 — Category', example: 'e.g. Direct Expenses' },
  { value: 3, label: 'Level 3 — Sub Category', example: 'e.g. Salary Expenses' },
  { value: 4, label: 'Level 4 — Item', example: 'e.g. Consultant Doctors' },
];

const levelBadge = {
  1: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  2: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  3: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  4: 'bg-slate-700/50 text-gray-400 border-slate-600',
};
const levelLabel = { 1: 'Main Section', 2: 'Category', 3: 'Sub Category', 4: 'Item' };

const RECORDS_PER_PAGE = 15;

const CategoryMaster = ({ isEmbedded = false, projectCategoryIds = null }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterMainSection, setFilterMainSection] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDragDrop, setShowDragDrop] = useState(false);
  const navigate = useNavigate();

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
      setCategories(res.data);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleRefresh = () => {
    setSearchTerm('');
    setFilterLevel('');
    setFilterMainSection('');
    setCurrentPage(1);
    fetchCategories();
  };

  // ── Build flat rows with full path ─────────────────────────────────────────
  const flatRows = useMemo(() => {
    const byId = {};
    const childrenMap = {};
    categories.forEach(c => {
      byId[c.id] = c;
      const pid = c.parent_id || 'root';
      if (!childrenMap[pid]) childrenMap[pid] = [];
      childrenMap[pid].push(c);
    });

    const getName = (id) => byId[id]?.name || '';

    const result = [];
    const traverse = (parentId, parentPrefix) => {
      const children = childrenMap[parentId] || [];
      // Sort children by sort_order ascending, fallback to id
      children.sort((a, b) => {
        if (a.sort_order !== b.sort_order) return (a.sort_order || 0) - (b.sort_order || 0);
        return a.id - b.id;
      });

      children.forEach((c, index) => {
        const currentPrefix = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;
        let mainSection = '', category = '', subCategory = '', item = '';
        let mainSectionId = null;
        if (c.level === 1) { 
          mainSection = c.name; mainSectionId = c.id; 
        } else if (c.level === 2) {
          const p = byId[c.parent_id];
          mainSection = getName(c.parent_id); category = c.name; mainSectionId = p?.id;
        } else if (c.level === 3) {
          const p2 = byId[c.parent_id];
          mainSection = getName(p2?.parent_id); category = p2?.name || ''; subCategory = c.name;
          mainSectionId = byId[p2?.parent_id]?.id;
        } else if (c.level === 4) {
          const p3 = byId[c.parent_id];
          const p2 = byId[p3?.parent_id];
          mainSection = getName(p2?.parent_id); category = p2?.name || ''; subCategory = p3?.name || ''; item = c.name;
          mainSectionId = byId[p2?.parent_id]?.id;
        }
        result.push({
          id: c.id, level: c.level, name: c.name,
          mainSection, category, subCategory, item, mainSectionId,
          sort_order: c.sort_order || 0, parent_id: c.parent_id,
          profit_perc: c.profit_perc,
          hierarchicalIndex: currentPrefix
        });
        traverse(c.id, currentPrefix);
      });
    };

    traverse('root', '');
    return result;
  }, [categories]);

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return flatRows.filter(r => {
      const matchSearch = !q || [r.mainSection, r.category, r.subCategory, r.item].some(v => v && v.toLowerCase().includes(q));
      const matchLevel = !filterLevel || r.level === Number(filterLevel);
      const matchMainSection = !filterMainSection || String(r.mainSectionId) === String(filterMainSection);
      const matchProject = !isEmbedded || !projectCategoryIds || projectCategoryIds.includes(r.id);
      return matchSearch && matchLevel && matchMainSection && matchProject;
    });
  }, [flatRows, searchTerm, filterLevel, filterMainSection, isEmbedded, projectCategoryIds]);

  const totalPages = Math.ceil(filtered.length / RECORDS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * RECORDS_PER_PAGE, currentPage * RECORDS_PER_PAGE);

  // ── Navigation helpers ─────────────────────────────────────────────────────
  const openAdd = () => { navigate('/add-category'); };
  const openEdit = (row) => { navigate('/add-category', { state: { editCategoryId: row.id } }); };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/categories/${confirmDelete.id}`);
      toast.success('Deleted!');
      setConfirmDelete(null);
      fetchCategories();
    } catch { toast.error('Delete failed'); }
  };

  const handleSaveOrder = async (updates) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/categories/reorder`, { items: updates });
      fetchCategories();
    } catch (err) {
      toast.error('Failed to save order');
    }
  };

  if (isEmbedded) {
    return (
      <>
        <div className="pb-6">

          {/* ── Toolbar ── */}
          <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-white">Categories</h2>
              <span className="text-slate-500 text-sm font-mono bg-slate-800/50 px-2.5 py-1 rounded-lg">{filtered.length} records</span>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {/* Level Filter */}
              <select value={filterLevel} onChange={e => { setFilterLevel(e.target.value); setCurrentPage(1); }}
                className="bg-slate-950 border border-slate-700 py-2 px-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition">
                <option value="">All Levels</option>
                {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label.split('—')[0].trim()}</option>)}
              </select>
              {/* Main Section Filter */}
              <select value={filterMainSection} onChange={e => { setFilterMainSection(e.target.value); setCurrentPage(1); }}
                className="bg-slate-950 border border-slate-700 py-2 px-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition max-w-[200px] truncate">
                <option value="">All Main Sections</option>
                {categories.filter(c => c.level === 1).map(l1 => <option key={l1.id} value={l1.id}>{l1.name}</option>)}
              </select>
              {/* Search */}
              <div className="relative">
                <input type="text" placeholder="Search..." value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="bg-slate-950 border border-slate-700 py-2 pl-9 pr-4 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition w-48" />
                <svg className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </div>
              <button onClick={() => setShowDragDrop(true)}
                className="bg-violet-600 hover:bg-violet-500 text-white font-bold py-2 px-5 rounded-xl transition flex items-center gap-2 text-sm shadow-lg shadow-violet-600/20">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" /></svg>
                Reorder
              </button>
              <button onClick={handleRefresh} title="Refresh Data"
                className="bg-slate-800 hover:bg-slate-700 text-white font-bold p-2.5 rounded-xl transition shadow-lg border border-slate-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              </button>
              {!isEmbedded && (
                <button onClick={openAdd}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-2 px-5 rounded-xl transition flex items-center gap-2 text-sm shadow-lg shadow-emerald-500/20">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add New Category
                </button>
              )}
            </div>
          </div>

          {/* ── Table ── */}
          <div className="bg-slate-900/60 border border-slate-700 rounded-2xl overflow-hidden backdrop-blur-sm shadow-xl">
            {loading ? (
              <div className="text-center py-20">
                <div className="w-8 h-8 rounded-full border-4 border-emerald-500/20 border-t-emerald-400 animate-spin mx-auto mb-3" />
                <p className="text-emerald-400 font-bold text-sm">Loading...</p>
              </div>
            ) : (
              <>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-800/80 border-b border-slate-700 text-xs uppercase tracking-widest text-gray-400">
                      <th className="py-3 px-4 text-left w-20">S.No</th>
                      <th className="py-3 px-4 text-left">Category Structure</th>
                      <th className="py-3 px-4 text-center w-44 whitespace-nowrap">Level</th>
                      <th className="py-3 px-4 text-right w-36">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {paginated.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-16">
                          <svg className="w-12 h-12 text-slate-700 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                          <p className="text-gray-500 text-sm">{searchTerm || filterLevel ? 'No results found.' : 'No categories yet. Click "+ Add New Category" to start.'}</p>
                        </td>
                      </tr>
                    ) : paginated.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-800/30 transition-colors group">
                        <td className="py-3 px-4 text-gray-500 font-mono text-xs whitespace-nowrap">{row.hierarchicalIndex}</td>
                        <td className="py-3 px-4">
                          <div style={{ paddingLeft: `${(row.level - 1) * 2.5}rem` }} className="flex items-center min-h-[1.5rem]">
                            {row.level === 1 && <span className="text-emerald-400 font-bold uppercase tracking-wide text-xs">{row.name}</span>}
                            {row.level === 2 && <span className="text-amber-300 font-semibold text-sm">{row.name}</span>}
                            {row.level === 3 && <span className="text-blue-300 text-sm">{row.name}</span>}
                            {row.level === 4 && <span className="text-gray-300 italic text-sm">{row.name}</span>}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-lg border whitespace-nowrap ${levelBadge[row.level]}`}>
                            L{row.level} — {levelLabel[row.level]}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEdit(row)}
                              className="text-blue-400 hover:text-blue-300 text-xs px-2.5 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20 transition flex items-center gap-1.5">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              Edit
                            </button>
                            <button onClick={() => setConfirmDelete({ id: row.id, name: row.name })}
                              className="text-rose-400 hover:text-rose-300 text-xs px-2.5 py-1 rounded-lg bg-rose-500/10 border border-rose-500/20 transition flex items-center gap-1.5">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={filtered.length} recordsPerPage={RECORDS_PER_PAGE} />
                )}
              </>
            )}
          </div>
        </div>

        {/* ══ CONFIRM DELETE MODAL ══════════════════════════════════════════════ */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-slate-900 border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
              <div className="bg-rose-500/10 p-5 flex items-center gap-4 border-b border-rose-500/20">
                <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400 flex-shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </div>
                <div>
                  <h3 className="text-rose-400 font-bold text-lg">Delete Category</h3>
                  <p className="text-slate-400 text-xs">This action cannot be undone</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-300 text-sm leading-relaxed">
                  Are you sure you want to delete <span className="text-white font-bold">"{confirmDelete.name}"</span>?
                  <span className="block mt-1.5 text-amber-400/70 text-xs">⚠ Sub-categories and items under it may also be deleted.</span>
                </p>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setConfirmDelete(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-gray-300 py-2.5 rounded-xl text-sm font-bold transition">Cancel</button>
                  <button onClick={handleDelete} className="flex-1 bg-rose-500 hover:bg-rose-400 text-white py-2.5 rounded-xl text-sm font-bold transition">Yes, Delete</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Layout title="Category Master" subtitle="Manage Financial Chart of Accounts (Levels 1 to 4)">
      <div className="pb-24">
        {!showDragDrop ? (
          <>
            <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-white">Categories</h2>
                <span className="text-slate-500 text-sm font-mono bg-slate-800/50 px-2.5 py-1 rounded-lg">{filtered.length} records</span>
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <select value={filterLevel} onChange={e => { setFilterLevel(e.target.value); setCurrentPage(1); }} className="bg-slate-950 border border-slate-700 py-2 px-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition">
                  <option value="">All Levels</option>
                  {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label.split('—')[0].trim()}</option>)}
                </select>
                <select value={filterMainSection} onChange={e => { setFilterMainSection(e.target.value); setCurrentPage(1); }} className="bg-slate-950 border border-slate-700 py-2 px-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition max-w-[200px] truncate">
                  <option value="">All Main Sections</option>
                  {categories.filter(c => c.level === 1).map(l1 => <option key={l1.id} value={l1.id}>{l1.name}</option>)}
                </select>
                <div className="relative">
                  <input type="text" placeholder="Search..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="bg-slate-950 border border-slate-700 py-2 pl-9 pr-4 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition w-48" />
                  <svg className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
                <button onClick={() => setShowDragDrop(true)}
                  className="bg-violet-600 hover:bg-violet-500 text-white font-bold py-2 px-5 rounded-xl transition flex items-center gap-2 text-sm shadow-lg shadow-violet-600/20">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" /></svg>
                  Reorder
                </button>
                <button onClick={handleRefresh} title="Refresh Data"
                  className="bg-slate-800 hover:bg-slate-700 text-white font-bold p-2.5 rounded-xl transition shadow-lg border border-slate-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                </button>
                <button onClick={openAdd} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-2 px-5 rounded-xl transition flex items-center gap-2 text-sm shadow-lg shadow-emerald-500/20">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add New Category
                </button>
              </div>
            </div>
            <div className="bg-slate-900/60 border border-slate-700 rounded-2xl overflow-hidden backdrop-blur-sm shadow-xl">
              {loading ? (<div className="text-center py-20"><div className="w-8 h-8 rounded-full border-4 border-emerald-500/20 border-t-emerald-400 animate-spin mx-auto mb-3" /><p className="text-emerald-400 font-bold text-sm">Loading...</p></div>) : (
                <><table className="w-full text-sm border-collapse"><thead><tr className="bg-slate-800/80 border-b border-slate-700 text-xs uppercase tracking-widest text-gray-400"><th className="py-3 px-4 text-left w-20">S.No</th><th className="py-3 px-4 text-left">Category Structure</th><th className="py-3 px-4 text-center w-44 whitespace-nowrap">Level</th><th className="py-3 px-4 text-right w-36">Actions</th></tr></thead><tbody className="divide-y divide-slate-800/50">{paginated.length === 0 ? (<tr><td colSpan={4} className="text-center py-16"><p className="text-gray-500 text-sm">{searchTerm || filterLevel ? 'No results.' : 'No categories yet.'}</p></td></tr>) : paginated.map((row) => (<tr key={row.id} className="hover:bg-slate-800/30 transition-colors group"><td className="py-3 px-4 text-gray-500 font-mono text-xs whitespace-nowrap">{row.hierarchicalIndex}</td><td className="py-3 px-4"><div style={{ paddingLeft: `${(row.level - 1) * 2.5}rem` }} className="flex items-center min-h-[1.5rem]">{row.level === 1 && <span className="text-emerald-400 font-bold uppercase tracking-wide text-xs">{row.name}</span>}{row.level === 2 && <span className="text-amber-300 font-semibold text-sm">{row.name}</span>}{row.level === 3 && <span className="text-blue-300 text-sm">{row.name}</span>}{row.level === 4 && <span className="text-gray-300 italic text-sm">{row.name}</span>}</div></td><td className="py-3 px-4 text-center whitespace-nowrap"><span className={`text-[10px] font-bold px-2.5 py-1 rounded-lg border whitespace-nowrap ${levelBadge[row.level]}`}>L{row.level} — {levelLabel[row.level]}</span></td><td className="py-3 px-4 text-right"><div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity"><button onClick={() => openEdit(row)} className="text-blue-400 hover:text-blue-300 text-xs px-2.5 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20 transition flex items-center gap-1.5">Edit</button><button onClick={() => setConfirmDelete({ id: row.id, name: row.name })} className="text-rose-400 hover:text-rose-300 text-xs px-2.5 py-1 rounded-lg bg-rose-500/10 border border-rose-500/20 transition flex items-center gap-1.5">Delete</button></div></td></tr>))}</tbody></table>
                  {totalPages > 1 && (
                    <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={filtered.length} recordsPerPage={RECORDS_PER_PAGE} />
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <CategoryDragDrop
            categories={categories}
            onSave={handleSaveOrder}
            onClose={() => setShowDragDrop(false)}
          />
        )}
      </div>
      {confirmDelete && (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"><div className="bg-slate-900 border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"><div className="bg-rose-500/10 p-5 flex items-center gap-4 border-b border-rose-500/20"><h3 className="text-rose-400 font-bold text-lg">Delete Category</h3></div><div className="p-6"><p className="text-gray-300 text-sm">Are you sure you want to delete <span className="text-white font-bold">"{confirmDelete.name}"</span>?</p><div className="flex gap-3 mt-6"><button onClick={() => setConfirmDelete(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-gray-300 py-2.5 rounded-xl text-sm font-bold transition">Cancel</button><button onClick={handleDelete} className="flex-1 bg-rose-500 hover:bg-rose-400 text-white py-2.5 rounded-xl text-sm font-bold transition">Yes, Delete</button></div></div></div></div>)}
    </Layout>
  );
};

export default CategoryMaster;
